// eslint-disable-next-line no-restricted-imports
import window from 'window-or-global';

import type { MacroAttributes } from '@atlaskit/editor-common/provider-factory/macro-provider';

import { expValEquals } from '@confluence/feature-experiments';
import { ACCESS_LINK } from '@confluence/named-routes/entry-points/routes.ts';

import { fetchMacroAttributes } from '../fetchMacroAttributes.ts';
import { wrapMacroParams } from '../transformers.ts';
import type { LegacyMacro } from '../transformers.ts';

/**
 * @see https://stash.atlassian.com/projects/CONFCLOUD/repos/confluence-jira-plugin/browse/confluence-jira-plugin/src/main/resources/editor-plugins/jira-paste-link.js#100
 */
type JiraMacroParams = {
	[otherParamName: string]: string;
	jqlQuery: string;
	server: string;
	serverId: string;
};

function convertLink(
	link: string,
	defaultJiraMacroAttributes: MacroAttributes,
): null | MacroAttributes {
	const noop = () => {};
	const legacyHandler = window.require('confluence/jim/editor-plugins/jira-paste-link');

	// jira paste handler needs object in this format
	const uri = {
		source: link,
	};
	const jimPasteHandlerResult: LegacyMacro & {
		params: JiraMacroParams;
	} = legacyHandler.pasteHandler(uri, null, noop);

	if (!jimPasteHandlerResult || !jimPasteHandlerResult.params) {
		return null;
	}

	/**
	 * use JSON.parse , JSON.stringify to deeply clone object, avoid any reference
	 */
	const macroAttributes: MacroAttributes = JSON.parse(JSON.stringify(defaultJiraMacroAttributes));

	if (!macroAttributes.attrs.parameters) {
		macroAttributes.attrs.parameters = {};
	}

	macroAttributes.attrs.parameters.macroParams = wrapMacroParams(jimPasteHandlerResult.params);

	return macroAttributes;
}

export async function createJiraConverter(
	contentId?: string,
): Promise<(link: string) => MacroAttributes | null> {
	const placeholderMacro = {
		macroDefinition: { name: 'jira' },
		contentId,
	};

	let jiraADFNode: MacroAttributes | null = null;
	try {
		// fetch ADF structure of JIM, and modify it to provide to editor later
		jiraADFNode = await fetchMacroAttributes(placeholderMacro);
	} catch (error) {
		// The placeholder endpoint does not honour a share token, so it 404s for these viewers.
		// Letting it reject would take the whole extension provider down with it.
		const isAccessLinkViewer =
			Boolean(window.location?.pathname && ACCESS_LINK.match(window.location.pathname)) &&
			expValEquals('cc_perms_ga_access_links', 'isEnabled', true);

		if (!isAccessLinkViewer) {
			throw error;
		}
	}

	if (!jiraADFNode) {
		return () => null;
	}

	const resolvedJiraADFNode = jiraADFNode;

	return (link: string) => convertLink(link, resolvedJiraADFNode);
}
